// ImageCompressor.js
import pako from 'pako';

/**
 * Class for compressing image frames using zlib compression
 * Compatible with Python's zlib.decompress()
 */
class ImageCompressor {
  /**
   * Creates a new ImageCompressor instance
   * @param {Object} defaultOptions - Default compression options
   * @param {number} defaultOptions.level - Compression level (1-9, where 9 is max)
   * @param {boolean} defaultOptions.raw - If true, creates raw deflate data
   */
  constructor(defaultOptions = { level: 6, raw: false }) {
    this.defaultOptions = defaultOptions;
  }

  /**
   * Compresses raw image frame data
   * @param {Uint8Array|ArrayBuffer|string|Blob} frameData - Raw image data to compress
   * @param {Object} options - Compression options (overrides defaults)
   * @returns {Uint8Array|Promise<Uint8Array>} - Compressed data
   */
  compressFrame(frameData, options = {}) {
    // Merge options with defaults
    const compressionOptions = { ...this.defaultOptions, ...options };
    
    // Process based on input type
    if (frameData instanceof Uint8Array) {
      return this._compressData(frameData, compressionOptions);
    } 
    else if (frameData instanceof ArrayBuffer) {
      const dataArray = new Uint8Array(frameData);
      return this._compressData(dataArray, compressionOptions);
    } 
    else if (typeof frameData === 'string') {
      return this._compressString(frameData, compressionOptions);
    } 
    else if (frameData instanceof Blob) {
      return this._compressBlob(frameData, compressionOptions);
    } 
    else {
      throw new Error('Unsupported input type. Please provide Uint8Array, ArrayBuffer, string, or Blob');
    }
  }

  /**
   * Handles compression of Uint8Array data
   * @private
   */
  _compressData(dataArray, options) {
    return pako.deflate(dataArray, options);
  }

  /**
   * Handles compression of string data
   * @private
   */
  _compressString(stringData, options) {
    // Handle base64 string
    if (stringData.indexOf('base64,') !== -1) {
      const base64 = stringData.split('base64,')[1];
      const binaryString = atob(base64);
      const dataArray = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        dataArray[i] = binaryString.charCodeAt(i);
      }
      return this._compressData(dataArray, options);
    } 
    // Handle plain string
    else {
      const dataArray = new TextEncoder().encode(stringData);
      return this._compressData(dataArray, options);
    }
  }

  /**
   * Handles compression of Blob data
   * @private
   */
  _compressBlob(blobData, options) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const dataArray = new Uint8Array(e.target.result);
          const result = this._compressData(dataArray, options);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(blobData);
    });
  }

  /**
   * Utility method to get compression stats
   * @param {Uint8Array} original - Original data
   * @param {Uint8Array} compressed - Compressed data
   * @returns {Object} Compression statistics
   */
  getCompressionStats(original, compressed) {
    const originalSize = original.length;
    const compressedSize = compressed.length;
    const compressionRatio = originalSize / compressedSize;
    const percentSaved = ((originalSize - compressedSize) / originalSize * 100).toFixed(2);
    
    return {
      originalSize,
      compressedSize,
      compressionRatio,
      percentSaved: `${percentSaved}%`
    };
  }
}

export default ImageCompressor;